import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDocument, useCollection, useFirestoreMutations } from '../../hooks/useFirestore'
import { SHIP_TYPES, CREW_ROLES } from '../../lib/shipTypes'
import ImageUploader from './ImageUploader'
import RichTextEditor from './RichTextEditor'
import './AdminEditor.css'

const EMPTY_SHIP = {
  name: '',
  type: 'galleon',
  description: '',
  imageUrl: '',
  arsenal: '',
  hidden: false,
  destroyed: false,
  sortOrder: 0,
  crew: []
}

export default function ShipEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const { data: existingShip, loading } = useDocument('ships', isNew ? null : id)
  const { data: characters } = useCollection('characters', { orderByField: 'name' })
  const { add, update, remove } = useFirestoreMutations('ships')

  const [ship, setShip] = useState(EMPTY_SHIP)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (existingShip) {
      setShip({ ...EMPTY_SHIP, ...existingShip })
    }
  }, [existingShip])

  const handleChange = (field, value) => {
    setShip(prev => ({ ...prev, [field]: value }))
  }

  const handleAddCrewMember = () => {
    setShip(prev => ({
      ...prev,
      crew: [...prev.crew, { characterId: '', role: 'crew' }]
    }))
  }

  const handleUpdateCrewMember = (index, field, value) => {
    setShip(prev => ({
      ...prev,
      crew: prev.crew.map((member, i) =>
        i === index ? { ...member, [field]: value } : member
      )
    }))
  }

  const handleRemoveCrewMember = (index) => {
    setShip(prev => ({
      ...prev,
      crew: prev.crew.filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      // Filter out crew members without a character selected
      const cleanedShip = {
        ...ship,
        crew: ship.crew.filter(m => m.characterId)
      }

      if (isNew) {
        await add(cleanedShip)
      } else {
        await update(id, cleanedShip)
      }

      navigate('/admin/ships')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${ship.name}"? This cannot be undone.`)) {
      return
    }

    setSaving(true)
    try {
      await remove(id)
      navigate('/admin/ships')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  // Get character name by ID
  const getCharacterName = (charId) => {
    const char = characters.find(c => c.id === charId)
    return char?.name || 'Unknown'
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>{isNew ? 'New Ship' : 'Edit Ship'}</h1>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => navigate('/admin/ships')}
        >
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="editor-form">
        {error && <div className="error-banner">{error}</div>}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Ship Name *</label>
            <input
              id="name"
              type="text"
              value={ship.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              placeholder="e.g., The Black Pearl"
            />
          </div>

          <div className="form-group">
            <label htmlFor="type">Ship Type</label>
            <select
              id="type"
              value={ship.type}
              onChange={(e) => handleChange('type', e.target.value)}
            >
              {SHIP_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group form-group-inline">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={ship.hidden === true}
                onChange={(e) => handleChange('hidden', e.target.checked)}
              />
              <span>Hidden (Spoiler)</span>
            </label>
            <span className="help-text">Only visible in admin</span>
          </div>

          <div className="form-group form-group-inline">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={ship.destroyed === true}
                onChange={(e) => handleChange('destroyed', e.target.checked)}
              />
              <span>Destroyed</span>
            </label>
            <span className="help-text">Mark ship as sunk/destroyed</span>
          </div>
        </div>

        <div className="form-group">
          <label>Ship Image</label>
          <ImageUploader
            value={ship.imageUrl}
            onChange={(url) => handleChange('imageUrl', url)}
            folder="ships"
          />
        </div>

        <div className="form-group">
          <label htmlFor="arsenal">Arsenal / Armaments</label>
          <textarea
            id="arsenal"
            value={ship.arsenal}
            onChange={(e) => handleChange('arsenal', e.target.value)}
            placeholder="e.g., 24 cannons, ballistae, Greek fire..."
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <RichTextEditor
            value={ship.description}
            onChange={(html) => handleChange('description', html)}
            placeholder="Describe the ship's history, appearance, notable features..."
          />
        </div>

        {/* Crew Section */}
        <fieldset className="form-section">
          <legend>Crew & Associations</legend>

          {ship.crew.length === 0 ? (
            <p className="text-secondary">No crew members added yet.</p>
          ) : (
            <div className="crew-list">
              {ship.crew.map((member, index) => (
                <div key={index} className="crew-member-row">
                  <select
                    value={member.characterId}
                    onChange={(e) => handleUpdateCrewMember(index, 'characterId', e.target.value)}
                    className="crew-character-select"
                  >
                    <option value="">Select character...</option>
                    {characters.map(char => (
                      <option key={char.id} value={char.id}>
                        {char.name} ({char.type?.toUpperCase()})
                      </option>
                    ))}
                  </select>

                  <select
                    value={member.role}
                    onChange={(e) => handleUpdateCrewMember(index, 'role', e.target.value)}
                    className="crew-role-select"
                  >
                    {CREW_ROLES.map(role => (
                      <option key={role.id} value={role.id}>{role.label}</option>
                    ))}
                  </select>

                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleRemoveCrewMember(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleAddCrewMember}
            style={{ marginTop: 'var(--space-md)' }}
          >
            + Add Crew Member
          </button>
        </fieldset>

        <div className="form-group">
          <label htmlFor="sortOrder">Sort Order</label>
          <input
            id="sortOrder"
            type="number"
            value={ship.sortOrder}
            onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
            style={{ maxWidth: '100px' }}
          />
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Ship' : 'Save Changes')}
          </button>

          {!isNew && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Ship
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
